<template>
  <v-container class="events-wrapper">
    <h3 class="events-title mb-4">
      <i class="fa-icon fa-regular fa-calendar"></i>
      {{ $t('upcomingEvents.title') }}
    </h3>

    <p v-if="events.length === 0" class="no-events-text text-center">
      {{ $t('upcomingEvents.noEvents') }}
    </p>
    <infinite-carousel v-else :speed="2" :auto-scroll="false" :item-height="90" :visible-items="visibleItemCount"
      class="events-carousel">
      <v-list dense>
        <template v-for="(event, index) in events">
          <v-list-item :key="event.id" class="event-item text-center" @click="$emit('event-click', event)"
            @mouseenter="focusEvent(index)" :class="{ 'event-focused': focusedIndex === index }">
            <v-list-item-content>
              <v-list-item-title class="event-title justify-center">
                <div class="title-wrapper">
                  <span class="event-name">{{ event.name }}</span>
                  <v-chip v-if="isEventNow(event)" x-small class="status-chip" color="error" label small>Now</v-chip>
                  <v-chip v-else-if="isEventToday(event)" x-small class="status-chip" color="warning" label
                    small>Soon</v-chip>
                </div>
              </v-list-item-title>
              <v-list-item-subtitle class="event-datetime justify-center">
                <v-icon small class="mr-1">mdi-calendar</v-icon>
                {{ formatDate(event.startDate) }}
                <br>
                <v-icon small class="mr-1">mdi-clock-outline</v-icon>
                {{ formatTime(event.startDate) }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-divider v-if="index < events.length - 1" :key="`divider-${event.id}`"
            class="mx-3 event-divider"></v-divider>
        </template>
      </v-list>
    </infinite-carousel>
  </v-container>
</template>

<script>
import InfiniteCarousel from '@/components/InfiniteCarousel.vue'

export default {
  name: 'Events',
  components: {
    InfiniteCarousel
  },
  props: {
    events: {
      type: Array,
      required: true,
      default: () => []
    },
    channelName: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      focusedIndex: null
    }
  },
  computed: {
    shouldScroll() {
      return this.events.length > 1
    },
    visibleItemCount() {
      // Show all items if 3 or fewer, otherwise show 3
      return 10;
    }
  },
  methods: {
    formatDate(dateString) {
      const date = new Date(dateString)
      return new Intl.DateTimeFormat(this.$i18n.locale, {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      }).format(date)
    },
    formatTime(dateString) {
      const date = new Date(dateString)
      return new Intl.DateTimeFormat(this.$i18n.locale, {
        hour: '2-digit',
        minute: '2-digit'
      }).format(date)
    },
    isEventToday(event) {
      const today = new Date()
      const eventDate = new Date(event.startDate)
      return (
        eventDate.getDate() === today.getDate() &&
        eventDate.getMonth() === today.getMonth() &&
        eventDate.getFullYear() === today.getFullYear() &&
        eventDate > today
      )
    },
    isEventNow(event) {
      const now = new Date()
      const eventStart = new Date(event.startDate)
      const eventEnd = event.endDate ? new Date(event.endDate) : new Date(eventStart.getTime() + 3600000) // default 1 hour
      return now >= eventStart && now <= eventEnd
    },
    focusEvent(index) {
      this.focusedIndex = index
    }
  }
}
</script>



<style scoped>
.events-wrapper {
  padding: 16px;
  padding-top: 0px;
}

.events-title {
  font-size: 24px;
  font-weight: bold;
  text-align: center;
  padding: 10px;
  padding-bottom: 0;
}

.events-container {
  background-color: var(--v-fullWhite-base);
  border: 1px solid #9A9A9A;
  border-radius: 15px;
  overflow: hidden;
}

.no-events-text {
  font-size: 18px;
  font-weight: bold;
  padding: 24px;
}

.events-carousel {
  border: none;
  border-radius: 0;
}

.event-item {
  transition: all 0.3s ease;
  border-radius: 8px;
  text-align: center;
  height: 90px;
  /* Match itemHeight prop */
  padding: 12px 0;
  /* Add padding for better spacing */
  margin: 4px 0;
}

.event-item:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.event-item :deep(.v-list-item__content) {
  display: flex;
  align-items: center;
  justify-content: center;
}

.event-item.event-focused {
  background-color: rgba(0, 0, 0, 0.05);
  transform: scale(1.02);
}

.event-title {
  position: relative;
  padding: 0 48px;
  /* Make room for the chip */
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
}

.event-datetime {
  color: rgba(0, 0, 0, 0.6);
  font-size: 14px !important;
  line-height: 1.6;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  /* Add bottom margin */
}

.event-divider {
  border-color: rgba(0, 0, 0, 0.12) !important;
  margin: 0 16px;
  opacity: 0.5;
  border-width: thin;
}

.v-chip {
  font-size: 10px !important;
}

.title-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.event-name {
  font-size: 16px;
  font-family: "Avenir", sans-serif;
  font-weight: 600;
}

.status-chip {
  position: absolute;
  right: 8px;
  font-size: 14px !important;
}

.v-list {
  background: transparent;
  padding: 4px 0;
}
</style>
